import React from 'react';
import { Immutable } from 'nuclear-js';
import ReactLoader from 'utils/react-loader';
import PropTypes from 'prop-types';
import CreateReactClass from 'create-react-class';

import Reactor from 'data/reactor';
import DataLoader from 'data/loader';
import RuntimeActions from 'data/app-runtime/actions';
import LCGetters from 'data/learning-continuum/getters';
import IAUtils from '../utils';
import CommonStrings from 'resources/common-strings';
import Analytics from 'utils/analytics';
import CommonValues from 'resources/common-values';
import HelpBox from 'components/generic/help/index';

import StudentGetters from 'data/student/getters';

import GoalSelection from './goal-selection';
import LearningStatements from './learning-statements';
import LearningStatementsFilters from './filters';
import SubAreaGroupFilter from './sub-area-group-filter';
import { deepCompare } from 'utils/react';

export default CreateReactClass({
    displayName: 'InstructionalAreasExpandedContainer',
    mixins: [Reactor.ReactMixin],

    propTypes: {
        currentTestEvent: PropTypes.instanceOf(Immutable.Map) /* Changes when term is changed */,
        studentInfo: PropTypes.instanceOf(
            Immutable.Map
        ) /* Changes shortly after new student is loaded (once data is received) */,
        studentBid: PropTypes.string.isRequired /* Changes when new student is loaded */,
        subject: PropTypes.instanceOf(Immutable.Map) /* Set once */,
        lcData: PropTypes.instanceOf(
            Immutable.Map
        ) /* Possibly updated after component update (see componentDidUpdate) */,
        expandedModule: PropTypes.string /* Not Used??? REMOVE */,
        selectedSubject: PropTypes.instanceOf(Immutable.Map).isRequired /* Not Used??? REMOVE */,
        selectGoals: PropTypes.func.isRequired /* Set once */,
        selectedGoals: PropTypes.instanceOf(
            Immutable.List
        ) /* Changed by the user in the UI (via props.selectGoals function) */,
        batchInfo: PropTypes.object /* Set once, Only exists when in Batch Print mode */,
        isSingleStudentMode: PropTypes.bool.isRequired,
    },

    getInitialState() {
        return {
            lcDataForTest: null,
            lcStandardTypes: Immutable.List(), // can contain 'topic', 'standard', or 'descartes'
            availableGrades: Immutable.List(),
            selectedGrades: Immutable.Set(),
            learningStatementGrouping: CommonValues.INSTRUCTION_AREAS_GROUPBY_TOPIC, // can be 'topic', 'standard', 'descartes'
            strengthFilter: Immutable.Set.of('develop'),
            selectedSubGoal: 'All',
            selectedTopicOrStandard: 'All',
            hideLearningStatements: false,
            isExpanded: false, // Track whether content is expanded or collapsed
        };
    },

    // --getDataBindings removal notes--
    // Typically when were pass studentInfo around the application, we actually mean StudentGetters.studentInfoForCurrentStudentBid.first().
    // This happens in app/components/student-profile/modules/index.jsx when rendering the <ModulesContainers>. In this instance of using studentInfoForCurrentStudentBid,
    // we do actually want the List of studentInfo objects and not just the first entry. I think removing this usage might take some more thinking.

    getDataBindings() {
        return {
            studentInfoAllTerms: StudentGetters.studentInfoForCurrentStudentBid,
        };
    },

    UNSAFE_componentWillMount() {
        this.initializeComponentState(this.props);
    },

    componentDidMount() {
        const currentTestEvent = this.props.currentTestEvent;
        if (currentTestEvent && !this.isLCDataLoaded(currentTestEvent.get('testkey'))) {
            DataLoader.loadLearningContinuumData(Immutable.List.of(currentTestEvent));
        }
    },

    componentDidUpdate(prevProps, prevState) {
        const currentTestEvent = this.props.currentTestEvent;
        if (currentTestEvent && !this.isLCDataLoaded(currentTestEvent.get('testkey'))) {
            DataLoader.loadLearningContinuumData(Immutable.List.of(currentTestEvent));
        }
    },

    getLCStandardTypes(lcDataForTest) {
        return lcDataForTest ? lcDataForTest.getIn(['statementType', 'StandardTypes'], null) : null;
    },

    getLearningStatementGrouping(lcStandardTypes) {
        if (__BATCH__ && lcStandardTypes && lcStandardTypes.contains(this.props.batchInfo.batchIAGroupBy)) {
            return this.props.batchInfo.batchIAGroupBy;
        } else {
            if (lcStandardTypes) {
                return IAUtils.defaultGroupBy(lcStandardTypes);
            } else {
                return CommonValues.INSTRUCTION_AREAS_GROUPBY_TOPIC;
            }
        }
    },

    UNSAFE_componentWillReceiveProps(nextProps) {
        // This path of code is where we diverge from the idea that changing runtime settings should only change a high
        //    level CSS class that controls which subject, student, term, or module that is displayed.  In this
        //    case, the requirements state when a subject/student/term is changed or the module is collapsed and
        //    re-expanded, the filters need to be reset.
        const resetState =
            // if the term changes, test event will change
            !Immutable.is(nextProps.currentTestEvent, this.props.currentTestEvent) ||
            // lcData changed
            !Immutable.is(nextProps.lcData, this.props.lcData);

        if (resetState) {
            this.initializeComponentState(nextProps);
            // The setTimeout is needed because componentWillReceiveProps is invoked during a dispatch.  This
            //     feels kind of hacky, but the alternative is to put this in a different lifecycle method, which
            //     would make the logic of what happens harder to follow.
            if (!nextProps.selectGoals || !nextProps.selectedGoals.isEmpty()) {
                setTimeout(() => this.props.selectGoals(Immutable.List()));
            }
        }
    },

    initializeComponentState(props) {
        const lcDataForTest = IAUtils.findLCDataForTestEvent(props.lcData, props.currentTestEvent);
        const grades = IAUtils.fetchGrades(lcDataForTest);
        const selectedGrades = grades.map((g) => g.get('name')).toSet();
        const lcStandardTypes = this.getLCStandardTypes(lcDataForTest);
        const learningStatementGrouping = this.getLearningStatementGrouping(lcStandardTypes);

        this.setState({
            lcDataForTest,
            availableGrades: grades,
            selectedGrades,
            lcStandardTypes,
            learningStatementGrouping,
            strengthFilter: Immutable.Set.of('develop'),
            hideLearningStatements: false,
            selectedSubGoal: 'All',
            selectedTopicOrStandard: 'All',
        });
    },

    onCollapse() {
        RuntimeActions.expandModule(null, null, true);
    },

    onTransitionEnd(event) {
        event.stopPropagation();
        Reactor.batch(() => {
            RuntimeActions.completeTransition();
            RuntimeActions.setAnimationIsRunning(false);
        });
    },

    onChevronClick(event) {
        this.setState({
            expandedYn: !this.state.expandedYn,
        });
    },

    onGroupByChange(groupBy) {
        this.setState({
            learningStatementGrouping: groupBy,
            selectedSubGoal: 'All',
            selectedTopicOrStandard: 'All',
        });
    },

    handleShowHideLCChange(showHideFlag) {
        this.setState({
            hideLearningStatements: showHideFlag,
        });
    },

    handleStrengthFilter(e) {
        const filterVal = e.target.id;
        let strengthFilter = this.state.strengthFilter;
        if (e.target.checked) {
            strengthFilter = strengthFilter.add(filterVal);
        } else {
            strengthFilter = strengthFilter.delete(filterVal);
        }
        if (strengthFilter.size == 0) {
            this.setState({
                strengthFilter: strengthFilter,
                selectedSubGoal: 'All',
                selectedTopicOrStandard: 'All',
            });
        } else {
            this.setState({
                strengthFilter: strengthFilter,
            });
        }
    },

    onDropdownHide(selectedGrades) {
        this.setState({ selectedGrades: selectedGrades });
    },

    isLCStatementTypeNone(lcStatementType) {
        return lcStatementType === 'none';
    },

    selectSubGoal(subgoal) {
        this.setState({ selectedSubGoal: subgoal });
    },

    selectTopicOrStandard(topicOrStandard) {
        this.setState({ selectedTopicOrStandard: topicOrStandard });
    },

    isLCDataLoaded(testkey) {
        return Reactor.evaluate(LCGetters.learningContinuumLoadedForTest(testkey));
    },

    helpText() {
        return (
            <div key="ia-expanded-help-content">
                <p key="ia-expanded-help-initial-text" className="help-box-p ia-expanded-help">
                    {CommonStrings.instructionalAreasExpandedHelp.initialText}
                </p>
                <ul key="ia-filter-ul" className="ia-expanded-strength">
                    <li key="ia-filter-li-standard">
                        <b> Standard </b>
                        {CommonStrings.instructionalAreasExpandedHelp.standardText}
                    </li>
                    <li key="ia-filter-li-filters">
                        <span key=" filter-icon" className="filter-icon" />
                        <b>Filters</b>
                        {CommonStrings.instructionalAreasExpandedHelp.filtersText}
                    </li>
                    <li key="ia-filter-li-reinforce">
                        <b>Reinforce</b>
                        {CommonStrings.instructionalAreasExpandedHelp.reinforceText}
                        <b>Introduce</b>
                        {CommonStrings.instructionalAreasExpandedHelp.introduceText}
                    </li>
                </ul>
                <div key="help-link-section" className="ia-expanded-help">
                    <strong key="read-more">{CommonStrings.instructionalAreasExpandedHelp.readMoreText}</strong>
                    <a key="help-link" href={CommonStrings.instructionalAreasExpandedHelp.helpLink} target="_blank">
                        {CommonStrings.instructionalAreasExpandedHelp.helpLinkText}
                    </a>
                </div>
            </div>
        );
    },

    renderHeader() {
        return (
            <div className="header-wrapper" key="instructional-area-header-container">
                <header key="instructional-area-header">
                    <h5>Instructional Areas</h5>
                    <HelpBox
                        id={'instructional-areas-expanded-help-box-header'}
                        appLevelCssClassName=""
                        helpContentsRenderer={this.helpText}
                        moduleName={CommonValues.SectionIDs.instructionalAreas}
                        helpLabel="instructional-areas-view-help"
                    />
                </header>
                <span
                    key="instructional-area-collapse"
                    className="icon-collapse"
                    onClick={() => {
                        this.onCollapse();
                        Analytics.recordButtonClick(
                            CommonValues.SectionIDs.instructionalAreas,
                            'close-module',
                            'instructional-areas-expanded'
                        );
                    }}
                >
                    {CommonStrings.collapse}
                </span>
            </div>
        );
    },

    renderSubAreaGroupFilters(learningStatements) {
        return (
            <SubAreaGroupFilter
                subject={this.props.subject}
                studentInfo={this.state.studentInfoAllTerms}
                lcStatements={learningStatements} // for now
                selectedGoals={this.props.selectedGoals}
                selectedSubGoal={this.state.selectedSubGoal}
                selectSubGoal={this.selectSubGoal}
                selectedTopicOrStandard={this.state.selectedTopicOrStandard}
                selectTopicOrStandard={this.selectTopicOrStandard}
                groupBy={this.state.learningStatementGrouping}
                key="sub-area-group-filter"
            />
        );
    },
   
    toggleExpand() {
        this.setState((prevState) => ({
            isExpanded: !prevState.isExpanded,
        }));
    },
    render() {
        const header = this.renderHeader();
        const goals = [];

        const { isExpanded } = this.state;

        let statementContent = null;
        let lsFilterContent = null;
        let copyRightContent = null;
        const selectedGoals = IAUtils.getSelectedGoals(this.props.selectedGoals, this.props.currentTestEvent);

        const lcDataForTest = this.state.lcDataForTest;
        const lcDataExists =
            lcDataForTest && lcDataForTest.get('statements') && !lcDataForTest.get('statements').isEmpty();

        const lcDataLoadCompleted =
            !this.props.currentTestEvent || this.isLCDataLoaded(this.props.currentTestEvent.get('testkey')); //if there is no test, there is nothing to load
        let lcGrouping = this.state.learningStatementGrouping;

        if (!this.props.currentTestEvent) {
            statementContent = <p className="no-test">{CommonStrings.noTestEvents}</p>;
        } else if (!lcDataExists) {
            statementContent = <p className="no-statements">{CommonStrings.noStatementsForTest}</p>;
        } else {
            const transformedLearningStatements = IAUtils.transformLearningStatements(
                lcDataForTest.get('statements'),
                this.props.currentTestEvent
            );
            let filteredLCStatements = IAUtils.getFilteredLCStatements(
                transformedLearningStatements,
                lcGrouping,
                this.props.selectedGoals,
                this.state.strengthFilter,
                this.state.selectedGrades,
                this.state.selectedSubGoal,
                this.state.selectedTopicOrStandard
            );
            if (
                __BATCH__ &&
                lcGrouping != CommonValues.INSTRUCTION_AREAS_GROUPBY_TOPIC &&
                (!filteredLCStatements || filteredLCStatements.isEmpty())
            ) {
                lcGrouping = CommonValues.INSTRUCTION_AREAS_GROUPBY_TOPIC;
                filteredLCStatements = IAUtils.getFilteredLCStatements(
                    transformedLearningStatements,
                    lcGrouping,
                    this.props.selectedGoals,
                    this.state.strengthFilter,
                    this.state.selectedGrades,
                    this.state.selectedSubGoal,
                    this.state.selectedTopicOrStandard
                );
            }
            const learningStatementMap = IAUtils.getLearningStatementMap(filteredLCStatements);

            if (selectedGoals) {
                selectedGoals.map((goal) => {
                    const learningStatementsForGoal = learningStatementMap.get(goal.get('goalkey'), null);
                    const goalContent = LearningStatements.getGoalsToShow(
                        learningStatementsForGoal,
                        goal,
                        this.props.selectedGoals,
                        this.props.studentInfo,
                        this.props.selectedSubGoal,
                        this.props.selectedTopicOrStandard,
                        lcGrouping,
                        this.state.hideLearningStatements,
                        this.state.strengthFilter,
                        this.state.lcStandardTypes,
                        this.props.batchInfo,
                        this.props.isSingleStudentMode
                    );
                    if (this.state.strengthFilter.size === 0 || goalContent.length > 0) {
                        goals.push(goalContent);
                    }
                });
            }

            const subAreaGroupFilters = this.renderSubAreaGroupFilters(filteredLCStatements);
            statementContent = (
                <div
                    key="instructional-area-goal-tab-container"
                    id={`instructional-area-goal-tab-container`}
                    className="ia-tab-container"
                >
                    {subAreaGroupFilters}
                    {goals}
                </div>
            );
            lsFilterContent = (
                <LearningStatementsFilters
                    groupBy={lcGrouping}
                    hideLearningStatements={this.state.hideLearningStatements}
                    onGroupByChange={this.onGroupByChange}
                    handleShowHideLCChange={this.handleShowHideLCChange}
                    handleStrengthFilter={this.handleStrengthFilter}
                    grades={this.state.availableGrades}
                    selectedGrades={this.state.selectedGrades}
                    onDropdownHide={this.onDropdownHide}
                    strengthFilter={this.state.strengthFilter}
                    currentTestEvent={this.props.currentTestEvent}
                    standardTypes={this.state.lcStandardTypes}
                    key="goal-selection"
                />
            );
        }
        
        copyRightContent = (
            <div>
                <p key="copyright-text" className="copyright screen-only">
                    {CommonStrings.copyright.copyrightText}
                </p>
                <p key="copyright-text-commonCore" className="copyright screen-only">
                    {CommonStrings.copyright.commonCore}
                </p>
                <p key="copyright-text-science" className="copyright screen-only">
                    {CommonStrings.copyright.science}
                </p>
                <p key="copyright-text-columbiaText" className="copyright screen-only">
                    {CommonStrings.copyright.columbiaText}
                </p>
                <div key="copyright-text-virginiaText" className="copyright screen-only">
                    {CommonStrings.copyright.virginiaText.split('\n').map((line, index) => (
                        <p class="virginiaTextAdjustment" key={`virginia-line-${index}`}>{line}</p>
                    ))}
                    <ul>
                        <li>
                            {CommonStrings.copyright.virginiaBulletPoint1}
                        </li>
                        <li>
                            {CommonStrings.copyright.virginiaBulletPoint2}
                        </li>
                        <li>
                            {CommonStrings.copyright.virginiaBulletPoint3}
                        </li>
                    </ul>
                    {CommonStrings.copyright.virginiaText1.split('\n').map((line, index) => (
                        <p class="virginiaTextAdjustment" key={`virginia-line-${index}`}>{line}</p>
                    ))}
                    <ul>
                        <li>
                            {CommonStrings.copyright.virginiaBulletPoint1}
                        </li>
                        <li>
                            {CommonStrings.copyright.virginiaBulletPoint2}
                        </li>
                        <li>
                            {CommonStrings.copyright.virginiaBulletPoint3}
                        </li>
                    </ul>
                </div>
            </div>
        );


        /*
            ReactLoader doesn't render it's content when in batch print mode.
            Therefore, it is necessary to test for the condition and not use it when appropriate.
         */
        const statementsContent = __BATCH__ ? (
            <div className="loader">{statementContent}</div>
        ) : (
            <ReactLoader loaded={lcDataLoadCompleted}>{statementContent}</ReactLoader>
        );

        return (
            <div
                className="module instructional-areas expanded"
                key="expanded-ia-panel"
                onTransitionEnd={this.onTransitionEnd}
            >
                {header}
                {lsFilterContent}
                <div
                    className="instructional-areas-content"
                    key="instructional-area-goal-container"
                    id="instructional-area-goal-container"
                >
                    <GoalSelection
                        currentTestEvent={this.props.currentTestEvent}
                        selectedGoals={selectedGoals}
                        selectGoals={this.props.selectGoals}
                        key="goal-selection"
                    />
                    {statementsContent}
                </div>
                <div className="copyright-container">
                    <div onClick={this.toggleExpand} className="icon-container" style={{ cursor: 'pointer' }}>
                        {isExpanded ? (
                            <span className="icon icon-collapse">x</span>
                        ) : (
                            <span className="icon icon-expand"></span>
                        )}
                    </div>
                    {isExpanded ? (
                        copyRightContent
                    ) : (
                        <div className="copyright screen-only">
                            {/* Show only the first few lines when collapsed */}
                            <p>{CommonStrings.copyright.copyrightText.split('\n')[0]}</p>
                            <p>{CommonStrings.copyright.commonCore.split('\n')[0]}</p>
                            <p>{CommonStrings.copyright.science.split('\n')[0]}</p>
                        </div>
                    )}
                </div>
            </div>
        );
        
    },
});